import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { api } from "./api";
import logo from "../assets/logoVerde.svg";
import fondo from "../assets/esquina.jpg";
import ComputerIcon from "@mui/icons-material/Computer";
import { useTurnstile } from "../hooks/useTurnstile";

const LoginWithoutPassword = ({ setUser }) => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [codigo, setCodigo] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [emailError, setEmailError] = useState("");
  const [codigoError, setCodigoError] = useState("");

  const { containerRef, token, loading: captchaLoading, failover } = useTurnstile();

  const showMessage = (message, severity = "info") => setSnackbar({ open: true, message, severity });
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const renderButtonContent = (text) =>
    loading ? <CircularProgress size={24} color="inherit" /> : text;

  // Paso 1: enviar OTP por email
  const handleSendOtp = async () => {
    setEmailError("");
    if (!email) {
      setEmailError("El email es obligatorio");
      return;
    }
    if (!token && !failover) {
      setEmailError("Por favor, completa el captcha");
      return;
    }
    setLoading(true);
    try {
      const bypass = failover ? true : false;

      const res = await api.auth.forgotPasswordEmail(email, token, bypass);

      if (res.error) {
        setEmailError(res.error);
        return;
      }

      showMessage("Código enviado a tu email", "success");
      setStep(2);
    } catch (err) {
      setEmailError(err.error || "Error enviando código");
    } finally {
      setLoading(false);
    }
  };

  // Paso 2: verificar OTP
  const handleVerifyOtp = async () => {
    setCodigoError("");
    if (!codigo) {
      setCodigoError("Debes ingresar el código");
      return;
    }
    setLoading(true);
    try {
      const res = await api.auth.checkEmailVerified(email, codigo);

      if (!res.token) {
        setCodigoError("Código incorrecto");
        return;
      }
      localStorage.setItem("token", res.token.trim());
    localStorage.setItem("user", JSON.stringify(res.user));
    setUser(res.user);
    
      showMessage("Código verificado. Ingresando...", "success");
      setTimeout(() => {
        navigate("/dashboard");
      }, 1500);
    } catch (err) {
      setCodigoError(err.error || "Error verificando código");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: `url(${fondo})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          bgcolor: "rgba(0,0,0,0.4)",
          zIndex: 1,
        },
      }}
    >
      <Box
        position="relative"
        zIndex={2}
        width="100%"
        maxWidth={430}
        sx={{ m: { xs: 2, sm: 3 } }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            padding: { xs: 3, sm: 4 },
            borderRadius: 2,
            boxShadow: 3,
            textAlign: "center",
          }}
        >
          <Box mb={1}>
            <img src={logo} alt="Logo" style={{ width: 80 }} />
          </Box>

          <Typography
            variant="subtitle1"
            mb={2}
            sx={{
              fontWeight: "bold",
              fontSize: { xs: 14, sm: 15 },
              lineHeight: 1.3,
            }}
          >
            Cooperativa de Servicios Públicos, Sociales y Vivienda de Avellaneda Ltda.
          </Typography>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            mb={2}
          >
            <ComputerIcon sx={{ color: "#6aae3b", mr: 1, fontSize: 26 }} />
            <Typography
              variant="h5"
              sx={{ color: "#6aae3b", fontWeight: 600 }}
            >
              Oficina Virtual
            </Typography>
          </Box>

          <Box
            sx={{
              width: 50,
              height: 2,
              backgroundColor: "#6aae3b",
              margin: "0 auto",
              borderRadius: 1,
              mb: 3,
            }}
          />

          {/* Paso 1: Email */}
          {step === 1 && (
            <>
              <Typography mb={2}>
                Ingresa tu email para recibir un código de acceso.
              </Typography>

              <TextField
                label="Email"
                fullWidth
                size="small"
                margin="dense"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError("");
                }}
                error={!!emailError}
                helperText={emailError}
                sx={{ "& .MuiInputBase-root": { height: 40 }, mb: 1 }}
              />

              {/* Captcha */}
              <Box display="flex" justifyContent="center" mb={2} minHeight={55}>
                {failover ? (
                  <Alert severity="warning">
                    No se pudo cargar el servicio de seguridad.
                  </Alert>
                ) : (
                  <div ref={containerRef} />
                )}
              </Box>

              <Button
                fullWidth
                variant="contained"
                onClick={handleSendOtp}
                disabled={(!token && !failover) || loading}
              >
                {renderButtonContent("Enviar código")}
              </Button>

              <Button
                fullWidth
                variant="text"
                sx={{ mt: 1 }}
                onClick={() => navigate("/login")}
              >
                Volver al login tradicional
              </Button>
            </>
          )}

          {/* Paso 2: Código */}
          {step === 2 && (
            <>
              <Typography mb={2}>
                Ingresa el código enviado a tu email.
              </Typography>

              <TextField
                label="Código"
                fullWidth
                size="small"
                value={codigo}
                margin="dense"
                onChange={(e) => {
                  setCodigo(e.target.value);
                  setCodigoError("");
                }}
                error={!!codigoError}
                helperText={codigoError}
                sx={{ "& .MuiInputBase-root": { height: 40 }, mb: 2 }}
              />

              <Button fullWidth variant="contained" onClick={handleVerifyOtp}>
                {renderButtonContent("Verificar código")}
              </Button>

              <Button fullWidth variant="text" sx={{ mt: 1 }} onClick={() => setStep(1)}>
                Volver
              </Button>
            </>
          )}

          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginWithoutPassword;
